import React from "react";
import { useSelector } from "react-redux";
import Back from "../components/Common/Back";
import { 
  FaWhatsapp, 
  FaPhone, 
  FaInstagram, 
  FaFacebook, 
  FaYoutube,
  FaMapMarkerAlt,
  FaClock
} from "react-icons/fa";
import useBackgroundImage from "../hooks/useBackgroundImage";
import { getFullScreenBackgroundStyle } from "../utils/backgroundStyles";

const Connect = () => {
  const { profile } = useSelector((state) => state.profile);
  const backgroundImage = useBackgroundImage();

  const handleWhatsApp = () => {
    const message = "Hello! I'd like to connect with you. 👋";
    const url = `https://wa.me/${profile?.whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const handleCall = () => {
    window.open(`tel:${profile?.mobile}`, "_blank");
  };

  const handleInstagram = () => {
    if (profile?.instagram) {
      window.open(profile.instagram, "_blank");
    }
  };

  const handleFacebook = () => {
    if (profile?.facebook) {
      window.open(profile.facebook, "_blank");
    }
  };

  const handleYoutube = () => {
    if (profile?.youtube) {
      window.open(profile.youtube, "_blank");
    }
  };

  const handleMap = () => {
    if (profile?.map_url) {
      window.open(profile.map_url, "_blank");
    }
  };

  return (

    <main 
      className="min-h-screen"
      style={getFullScreenBackgroundStyle(backgroundImage)}
    >
      <div className="max-w-screen-xl pt-20 md:pt-24 lg:pt-32 pb-24 md:pb-20 lg:pb-32 mx-auto px-3 md:px-4 lg:px-6">
          <Back />
          
          {/* Header */}
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-black mb-4 poppins drop-shadow-lg">
              Connect With Us
            </h1>
            <p className="text-white/90 text-lg md:text-xl poppins">
              Stay connected and follow us on social media
            </p>
          </div>

          {/* Contact Cards Grid */}
          <div className="grid grid-cols-3 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-6 lg:gap-8">
            
            {/* WhatsApp */}
            <div className="bg-white/90 backdrop-blur-md rounded-lg md:rounded-2xl p-2 md:p-6 border border-white/30 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
              <div className="text-center">
                <div className="w-8 h-8 md:w-16 md:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-4">
                  <FaWhatsapp className="w-4 h-4 md:w-8 md:h-8 text-green-600" />
                </div>
                <h3 className="text-xs md:text-xl font-bold text-gray-800 poppins mb-1 md:mb-2">WhatsApp</h3>
                <p className="text-gray-600 text-[8px] md:text-sm mb-2 md:mb-4 poppins hidden md:block">Chat with us directly</p>
                <button
                  onClick={handleWhatsApp}
                  className="w-full px-2 py-1.5 md:px-4 md:py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg md:rounded-xl font-semibold transition-all duration-300 hover:scale-105 poppins text-[8px] md:text-sm"
                >
                  Start Chat
                </button>
              </div>
            </div>

            {/* Phone Call */}
            <div className="bg-white/90 backdrop-blur-md rounded-lg md:rounded-2xl p-2 md:p-6 border border-white/30 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
              <div className="text-center">
                <div className="w-8 h-8 md:w-16 md:h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-4">
                  <FaPhone className="w-4 h-4 md:w-8 md:h-8 text-purple-600 transform rotate-90" />
                </div>
                <h3 className="text-xs md:text-xl font-bold text-gray-800 poppins mb-1 md:mb-2">Call Us</h3>
                <p className="text-gray-600 text-[8px] md:text-sm mb-2 md:mb-4 poppins hidden md:block">Speak with our team</p>
                <button
                  onClick={handleCall}
                  className="w-full px-2 py-1.5 md:px-4 md:py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg md:rounded-xl font-semibold transition-all duration-300 hover:scale-105 poppins text-[8px] md:text-sm"
                >
                  Call Now
                </button>
              </div>
            </div>

            {/* Instagram */}
            <div className="bg-white/90 backdrop-blur-md rounded-lg md:rounded-2xl p-2 md:p-6 border border-white/30 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
              <div className="text-center">
                <div className="w-8 h-8 md:w-16 md:h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-4">
                  <FaInstagram className="w-4 h-4 md:w-8 md:h-8 text-white" />
                </div>
                <h3 className="text-xs md:text-xl font-bold text-gray-800 poppins mb-1 md:mb-2">Instagram</h3>
                <p className="text-gray-600 text-[8px] md:text-sm mb-2 md:mb-4 poppins hidden md:block">Follow our journey</p>
                <button
                  onClick={handleInstagram}
                  disabled={!profile?.instagram}
                  className={`w-full px-2 py-1.5 md:px-4 md:py-3 rounded-lg md:rounded-xl font-semibold transition-all duration-300 hover:scale-105 poppins text-[8px] md:text-sm ${
                    profile?.instagram 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {profile?.instagram ? 'Follow Us' : 'Coming Soon'}
                </button>
              </div>
            </div>

            {/* Facebook */}
            <div className="bg-white/90 backdrop-blur-md rounded-lg md:rounded-2xl p-2 md:p-6 border border-white/30 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
              <div className="text-center">
                <div className="w-8 h-8 md:w-16 md:h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-4">
                  <FaFacebook className="w-4 h-4 md:w-8 md:h-8 text-indigo-600" />
                </div>
                <h3 className="text-xs md:text-xl font-bold text-gray-800 poppins mb-1 md:mb-2">Facebook</h3>
                <p className="text-gray-600 text-[8px] md:text-sm mb-2 md:mb-4 poppins hidden md:block">Like our page</p>
                <button
                  onClick={handleFacebook}
                  disabled={!profile?.facebook}
                  className={`w-full px-2 py-1.5 md:px-4 md:py-3 rounded-lg md:rounded-xl font-semibold transition-all duration-300 hover:scale-105 poppins text-[8px] md:text-sm ${
                    profile?.facebook 
                      ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {profile?.facebook ? 'Like Us' : 'Coming Soon'}
                </button>
              </div>
            </div>

            {/* YouTube */}
            <div className="bg-white/90 backdrop-blur-md rounded-lg md:rounded-2xl p-2 md:p-6 border border-white/30 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
              <div className="text-center">
                <div className="w-8 h-8 md:w-16 md:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-4">
                  <FaYoutube className="w-4 h-4 md:w-8 md:h-8 text-red-600" />
                </div>
                <h3 className="text-xs md:text-xl font-bold text-gray-800 poppins mb-1 md:mb-2">YouTube</h3>
                <p className="text-gray-600 text-[8px] md:text-sm mb-2 md:mb-4 poppins hidden md:block">Watch our videos</p>
                <button
                  onClick={handleYoutube}
                  disabled={!profile?.youtube}
                  className={`w-full px-2 py-1.5 md:px-4 md:py-3 rounded-lg md:rounded-xl font-semibold transition-all duration-300 hover:scale-105 poppins text-[8px] md:text-sm ${
                    profile?.youtube 
                      ? 'bg-red-600 hover:bg-red-700 text-white' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {profile?.youtube ? 'Subscribe' : 'Coming Soon'}
                </button>
              </div>
            </div>

            {/* Location */}
            <div className="bg-white/90 backdrop-blur-md rounded-lg md:rounded-2xl p-2 md:p-6 border border-white/30 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
              <div className="text-center">
                <div className="w-8 h-8 md:w-16 md:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-4">
                  <FaMapMarkerAlt className="w-4 h-4 md:w-8 md:h-8 text-green-600" />
                </div>
                <h3 className="text-xs md:text-xl font-bold text-gray-800 poppins mb-1 md:mb-2">Visit Us</h3>
                <p className="text-gray-600 text-[8px] md:text-sm mb-2 md:mb-4 poppins hidden md:block">Find our location</p>
                <button
                  onClick={handleMap}
                  disabled={!profile?.map_url}
                  className={`w-full px-2 py-1.5 md:px-4 md:py-3 rounded-lg md:rounded-xl font-semibold transition-all duration-300 hover:scale-105 poppins text-[8px] md:text-sm ${
                    profile?.map_url 
                      ? 'bg-green-600 hover:bg-green-700 text-white' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {profile?.map_url ? 'Get Directions' : 'Location TBA'}
                </button>
              </div>
            </div>
          </div>

          {/* Store Information */}
          <div className="mt-6 md:mt-16">
            <div className="bg-white/90 backdrop-blur-md rounded-lg md:rounded-2xl p-4 md:p-8 border border-white/30 shadow-lg">
              <h2 className="text-lg md:text-3xl font-bold text-gray-800 poppins mb-4 md:mb-6 text-center">
                Store Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
                {/* Store Details */}
                <div>
                  <h3 className="text-sm md:text-xl font-semibold text-gray-800 poppins mb-3 md:mb-4">About Us</h3>
                  <div className="space-y-2 md:space-y-3">
                    <div className="flex items-center">
                      <FaMapMarkerAlt className="w-4 h-4 md:w-5 md:h-5 text-purple-600 mr-2 md:mr-3" />
                      <span className="text-gray-700 poppins text-xs md:text-base">{profile?.address || "Address not available"}</span>
                    </div>
                    <div className="flex items-center">
                      <FaPhone className="w-4 h-4 md:w-5 md:h-5 text-indigo-600 mr-2 md:mr-3 transform rotate-90" />
                      <span className="text-gray-700 poppins text-xs md:text-base">{profile?.mobile || "Phone not available"}</span>
                    </div>
                    <div className="flex items-center">
                      <FaClock className="w-4 h-4 md:w-5 md:h-5 text-purple-600 mr-2 md:mr-3" />
                      <span className="text-gray-700 poppins text-xs md:text-base">
                        {profile?.from_time && profile?.to_time 
                          ? `${profile.from_time} - ${profile.to_time}` 
                          : "Hours not available"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Store Description */}
                <div>
                  <h3 className="text-sm md:text-xl font-semibold text-gray-800 poppins mb-3 md:mb-4">Welcome</h3>
                  <p className="text-gray-700 poppins leading-relaxed text-xs md:text-base">
                    Thank you for choosing {profile?.name || "our restaurant"}! We're excited to connect with you 
                    and share our passion for great food and excellent service. Follow us on social media to stay 
                    updated with our latest offerings, special promotions, and behind-the-scenes content.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
  );
};

export default Connect;
